<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';

import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';
import ContactHistory from 'dashboard/components-next/Contacts/ContactsSidebar/ContactHistory.vue';
import DealLinkedEntityTab from 'dashboard/components-next/Deals/DealDetail/DealLinkedEntityTab.vue';
import DealForm from 'dashboard/components-next/Deals/DealForm/DealForm.vue';
import DealDeleteSection from 'dashboard/components-next/Deals/DealDetail/DealDeleteSection.vue';
import LinkedTasks from 'dashboard/components-next/Tasks/LinkedTasks.vue';

const props = defineProps({
  deal: { type: Object, required: true },
  isUpdating: { type: Boolean, default: false },
});

const emit = defineEmits(['back', 'updated', 'deleted']);

const { t } = useI18n();
const store = useStore();

const dealFormRef = ref(null);
const isFormInvalid = computed(() => dealFormRef.value?.isFormInvalid);

const displayName = computed(
  () => props.deal.name || t('DEALS.EMPTY_STATE.TITLE')
);

// --- Tabs: Detalhes / Contato / Empresa / Tarefas / Conversas ---
const TABS_OPTIONS = [
  'details',
  'contact',
  'company',
  'tasks',
  'conversations',
];
const activeTab = ref('details');
const tasksCount = ref(0);

const conversationsByContactId = useMapGetter(
  'contactConversations/getAllConversationsByContactId'
);
const conversationsCount = computed(
  () => conversationsByContactId.value(props.deal.contactId)?.length || 0
);

const tabs = computed(() =>
  TABS_OPTIONS.map(value => {
    let count;
    if (value === 'contact') count = props.deal.contact ? 1 : undefined;
    if (value === 'company') count = props.deal.companies?.length || undefined;
    if (value === 'tasks') count = tasksCount.value || undefined;
    if (value === 'conversations') {
      count =
        conversationsCount.value > 0 ? conversationsCount.value : undefined;
    }
    return {
      label:
        value === 'tasks'
          ? t('TASKS.HEADER')
          : t(`DEALS.SIDEBAR.TABS.${value.toUpperCase()}`),
      count,
      value,
    };
  })
);

const activeTabIndex = computed(() => TABS_OPTIONS.indexOf(activeTab.value));

const fetchConversations = () => {
  if (props.deal.contactId) {
    store.dispatch('contactConversations/get', props.deal.contactId);
  }
};

watch(() => props.deal.contactId, fetchConversations);
watch(
  () => props.deal.id,
  () => {
    activeTab.value = 'details';
  }
);
onMounted(fetchConversations);

const handleSave = async () => {
  const updated = await dealFormRef.value?.save();
  if (updated) emit('updated', updated);
};
</script>

<template>
  <div
    class="thin-scrollbar flex flex-col gap-4 w-full h-full overflow-y-auto border-r border-n-weak bg-n-solid-1 p-4"
  >
    <div class="flex items-center gap-2">
      <Button
        icon="i-lucide-arrow-left"
        variant="ghost"
        color="slate"
        size="xs"
        @click="emit('back')"
      />
      <Avatar :name="displayName" :size="40" rounded-full />
      <div class="flex flex-col min-w-0">
        <span
          class="flex items-center gap-1.5 text-sm font-medium text-n-slate-12"
        >
          <span class="truncate">{{ displayName }}</span>
          <span
            class="i-lucide-dollar-sign size-4 shrink-0 text-n-slate-11"
            aria-hidden="true"
          />
        </span>
      </div>
    </div>

    <TabBar
      :tabs="tabs"
      :initial-active-tab="activeTabIndex"
      class="w-full [&>button]:w-full bg-n-alpha-black2"
      @tab-changed="tab => (activeTab = tab.value)"
    />

    <div v-if="activeTab === 'details'" class="flex flex-col gap-4">
      <DealForm ref="dealFormRef" :deal-data="deal" />
      <Button
        :label="t('DEALS.LIST.SAVE')"
        size="sm"
        :is-loading="isUpdating"
        :disabled="isUpdating || isFormInvalid"
        @click="handleSave"
      />
    </div>
    <DealDeleteSection
      v-if="activeTab === 'details'"
      :selected-deal="{ id: deal.id, name: deal.name }"
      @deleted="emit('deleted')"
    />

    <DealLinkedEntityTab
      v-if="activeTab === 'contact' || activeTab === 'company'"
      :key="activeTab"
      :deal="deal"
      :kind="activeTab"
      @updated="updated => emit('updated', updated)"
    />

    <LinkedTasks
      v-show="activeTab === 'tasks'"
      :deal-id="deal.id"
      @loaded="count => (tasksCount = count)"
    />

    <ContactHistory
      v-if="activeTab === 'conversations'"
      :contact-id="deal.contactId"
    />
  </div>
</template>
